import type { IResponseEventStatsTeamValue, IStatsRepresentation } from "~/types";
import formatNumber from "~/common/helpers/format-number";
import { convertSecondsToTime, convertTimeToSeconds, getMinutesFromHours } from "~/videos/helpers/format-time";

interface ItemStatSVG {
  type: "svg";
  displayValue: string;
}

interface ItemStatProgress {
  type: "progress";
  numericValue: number;
  displayValue: string;
  playlistId?: string;
}

interface ItemStatText {
  type: "text";
  displayValue: string;
  playlistId?: string;
}

export type ItemStatValue = ItemStatSVG | ItemStatProgress | ItemStatText;

export interface IStatValueOptions {
  playlistId?: string | null;
  representation?: IStatsRepresentation;
  isDecimalFormat?: boolean;
}
export const getNumericValue = (v?: number | string): number => (Number.isNaN(Number(v)) ? 0 : Number(formatNumber(v)));

export const getValueFromPercentageString = <T extends boolean = false>(
  str: string,
  asNumber: T = false as T,
  isDecimalFormat: boolean = false
): T extends true ? number : string => {
  if (!str || str.length === 0) return (asNumber ? 0 : "") as T extends true ? number : string;

  // Try to extract a clean numeric part (first valid number with optional %)
  const trimmed = str.trim();
  const match = trimmed.match(/^(-?\d+(?:[.,]\d+)?)(%)?$/) || trimmed.match(/(-?\d+(?:[.,]\d+)?)%?/);
  const extractedValue = match ? match[1].replace(",", ".") : "-";

  let value: number = Number(extractedValue);

  // If no valid number, return original string
  const tooMuchSeparators = trimmed.split(/[.,]/).length > 2; // if there are more than 2 parts, there are more than 1 separator (.,) in the number
  if (Number.isNaN(value) || tooMuchSeparators) return (asNumber ? 0 : str) as T extends true ? number : string;

  // Convert decimal value if decimal format requested and value is valid decimal (e.g. not already converted percentage or too high value)
  const isValidDecimal = !str.includes("%") && Math.abs(value) <= 10; // Comparing with 10 used to permit processing values like 1.2 as 120%, 2.0 as 200%, 6.7 as 670%, etc. when it should be processed as decimal.
  if (isDecimalFormat && isValidDecimal) {
    value *= 100;
  }

  let result: number | string = value;
  // If value has more than 2 decimal places, round to 2 decimal places
  if (/\.\d{3,}/.test(String(value))) {
    result = Number(result).toFixed(2);
    // If the value has more than 2 decimal places and the last 2 digits are 00, remove the last 2 digits
    if (result.split(".")[1] === "00") {
      result = result.split(".")[0];
    }
  }
  result = asNumber ? getNumericValue(result) : String(result);

  return result as T extends true ? number : string;
};

export const getTimeValue = (text: string, number: number = 0): string => {
  let value = text;
  const isValidTimeFormat = /^([0-1][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])$/.test(text);
  const isValidNumber = !Number.isNaN(Number(text));

  if (number === 0 && !isValidTimeFormat && isValidNumber) {
    // If the numeric value is missed or 0 and the time format is not used, convert the text value (number) to time
    value = convertSecondsToTime(Number(text));
  } else if (number > 0) {
    // If the numeric value is present and greater than 0, convert the number to time
    value = convertSecondsToTime(number);
  } else {
    // Otherwise, get the mm:ss from the text value (time format)
    // If the time format is invalid, the original text value is returned
    value = getMinutesFromHours(text);
  }

  return value;
};
export const getPlaylistIdForStatItem = (value: unknown, options?: IStatValueOptions): string | undefined => {
  if (!value || !options?.playlistId?.trim()) return undefined;

  const str = String(value);
  const isTime = options.representation === "time" || str.includes(":");
  const isPercent = options.representation === "percent" || str.includes("%");

  if (isTime) return convertTimeToSeconds(str) ? options.playlistId : undefined;
  if (isPercent) return getValueFromPercentageString(str, true, options?.isDecimalFormat) ? options.playlistId : undefined;

  const isNonZeroCharString = !/^[0\s.,]+$/.test(str);
  const isNonZeroNumber = Number(str) && !Number.isNaN(Number(str));
  return isNonZeroCharString || isNonZeroNumber ? options.playlistId : undefined;
};

export const getTextStatItem = (value: unknown, options?: IStatValueOptions): ItemStatValue => {
  const item: ItemStatText = { type: "text", displayValue: value ? String(value) : "" };
  const playlistId = getPlaylistIdForStatItem(value, options);

  if (playlistId) item.playlistId = playlistId;

  return item;
};

export default function getStatValue(v: any, options?: IStatValueOptions): ItemStatValue {
  let value = v;

  switch (typeof value) {
    case "object": {
      if (v === null) return getTextStatItem(value, options);

      const text: string = value.text || value.displayValue || "";
      const number: number = v.number || v.numericValue || 0;

      if (options?.representation === "time") {
        value = getTimeValue(text, number);
      } else if (options?.representation === "percent") {
        value = getValueFromPercentageString(text, false, options?.isDecimalFormat);
      } else {
        value = v.text || v.displayValue || "";
      }

      return { type: "progress", numericValue: number, displayValue: value, playlistId: getPlaylistIdForStatItem(value, options) };
    }

    // biome-ignore lint/suspicious/noFallthroughSwitchClause: false positive
    case "string": {
      // First of all check for SVG, no matter what `representation` is
      // Also note: `playlistId` will not be handled for 'SVG' items
      if (value.includes("<svg")) {
        const item: ItemStatSVG = { type: "svg", displayValue: value };
        return item;
      }

      if (options?.representation === "percent") {
        const item: ItemStatProgress = {
          type: "progress",
          numericValue: getValueFromPercentageString(value, true, options?.isDecimalFormat),
          displayValue: getValueFromPercentageString(value, false, options?.isDecimalFormat),
        };
        const playlistId = getPlaylistIdForStatItem(value, options);
        if (playlistId) item.playlistId = playlistId;
        return item;
      }
      if (options?.representation === "time") {
        const item: ItemStatProgress = { type: "progress", numericValue: 0, displayValue: getTimeValue(value) };
        const playlistId = getPlaylistIdForStatItem(value, options);
        if (playlistId) item.playlistId = playlistId;
        return item;
      }
      if (options?.representation === "number") {
        const item: ItemStatProgress = { type: "progress", numericValue: parseFloat(value), displayValue: formatNumber(value) };
        const playlistId = getPlaylistIdForStatItem(value, options);
        if (playlistId) item.playlistId = playlistId;
        return item;
      }

      // If no `representation` passed - try to parse the value
      // Assuming it's a `number` format
      if (value === "") {
        return getTextStatItem(value, options);
      }
      if (!Number.isNaN(Number(value))) {
        const numericValue = getNumericValue(value);
        const item: ItemStatProgress = { type: "progress", numericValue, displayValue: String(numericValue) };
        const playlistId = getPlaylistIdForStatItem(value, options);
        if (playlistId) item.playlistId = playlistId;
        return item;
      }
      // Assuming it's a `percentage` format
      if (value.includes("%")) {
        const item: ItemStatProgress = {
          type: "progress",
          numericValue: getValueFromPercentageString(value, true, options?.isDecimalFormat),
          displayValue: getValueFromPercentageString(value, false, options?.isDecimalFormat),
        };
        const playlistId = getPlaylistIdForStatItem(value, options);
        if (playlistId) item.playlistId = playlistId;
        return item;
      }
      // Assuming it's a `time` format
      if (value.includes(":")) {
        const item: ItemStatProgress = { type: "progress", numericValue: 0, displayValue: getMinutesFromHours(value) };
        const playlistId = getPlaylistIdForStatItem(value, options);
        if (playlistId) item.playlistId = playlistId;
        return item;
      }
    }
    // eslint-disable-next-line no-fallthrough
    case "number": {
      const numericValue = getNumericValue(value);
      const item: ItemStatProgress = { type: "progress", numericValue, displayValue: String(numericValue) };

      if (options?.representation === "percent") {
        item.displayValue = getValueFromPercentageString(String(value), false, options?.isDecimalFormat);
      } else if (options?.representation === "time") {
        item.displayValue = convertSecondsToTime(Number(value));
      }

      const playlistId = getPlaylistIdForStatItem(value, options);
      if (playlistId) item.playlistId = playlistId;
      return item;
    }

    default: {
      return getTextStatItem(value, options);
    }
  }
}

export function getStatValueV3(v: IResponseEventStatsTeamValue, options?: IStatValueOptions): ItemStatValue {
  let numericValue = getNumericValue(v.numericValue || v.displayValue) || 0;
  let displayValue = v.displayValue;

  if (options?.representation === "percent" || v.displayValue.includes("%")) {
    // Note that we're using `true` for `isDecimalFormat` here to handle decimal format percentages correctly,
    // because we expect to always receive percent stats in decimal format for team stats, unlike for other stats
    numericValue = getValueFromPercentageString(String(numericValue), true, true);
    displayValue = getValueFromPercentageString(displayValue, false, true);
  }

  if (options?.representation === "time" || v.displayValue.includes(":")) {
    numericValue = convertTimeToSeconds(displayValue) || 0;
    displayValue = getMinutesFromHours(displayValue);
  }

  return {
    type: v.type,
    numericValue,
    displayValue,
    playlistId: getPlaylistIdForStatItem(v.displayValue, options),
  };
}
